import { z } from 'zod';
import { dateRange, pageInfo } from '../../schema/filters.js';
import { helperSchema } from '../../../zodExtensions.js';
// --------------- Webhook Event Topics ----------------
export const ConfigWebhookTopic = {
    Claim_Created: 'Claim_Created',
    Claim_Finalized: 'Claim_Finalized',
    Claim_Updated: 'Claim_Updated',
    Shipping_Claim_Created: 'Shipping_Claim_Created',
    Shipping_Claim_Finalized: 'Shipping_Claim_Finalized',
    Shipping_Claim_Updated: 'Shipping_Claim_Updated',
    Registration_Created: 'Registration_Created',
};
export const ConfigWebhookDeliveryStatus = {
    Pending: 'Pending',
    Processing: 'Processing',
    Delivered: 'Delivered',
    Failed: 'Failed',
};
const webhookTopicSchema = z.enum(ConfigWebhookTopic);
// --------------- Config Webhook Event ----------------
export const configWebhookEvent = z.object({
    id: z.number(),
    topic: webhookTopicSchema,
    isEnabled: z.boolean(),
});
export const configWebhookEventCreate = z.object({
    topic: webhookTopicSchema,
    isEnabled: z.boolean(),
});
export const configWebhookEventUpdate = z.object({
    id: z.number(),
    isEnabled: z.boolean(),
});
// --------------- Config Webhook ----------------
export const configWebhook = z.object({
    id: z.number(),
    name: z.string(),
    endpointUrl: z.url(),
    hmacSignatureSecret: z.string(),
    configWebhookEvents: z.array(configWebhookEvent),
});
export const configWebhookCreate = z.object({
    name: helperSchema.nonEmptyString,
    endpointUrl: z.url(),
    configWebhookEvents: z
        .array(configWebhookEventCreate)
        .min(1, 'At least one topic must be selected'),
});
export const configWebhookUpdate = z.object({
    name: helperSchema.nonEmptyString.optional(),
    endpointUrl: z.url().optional(),
    configWebhookEvents: z.array(configWebhookEventUpdate).optional(),
});
// --------------- Config Webhook Event CRUD ----------------
export const configWebhookEventCreateBody = configWebhookEventCreate;
export const configWebhookEventUpdateBody = z.object({
    isEnabled: z.boolean(),
});
// --------------- Config Webhook Event Delivery (audit) ----------------
export const configWebhookDelivery = z.object({
    id: z.number(),
    topic: z.string().nullable(),
    status: z.enum(ConfigWebhookDeliveryStatus),
    payload: z.unknown().nullable(),
    responseStatus: z.number().nullable(),
    responseBody: z.string().nullable(),
    responseContentType: z.string().nullable(),
    createdOn: z.string(),
    lastAttemptOn: z.string().nullable(),
    nextRetryOn: z.string().nullable(),
    attempts: z.number(),
    canRetry: z.boolean(),
});
// --------------- Config Webhook Event Delivery search ----------------
/**
 * Buckets rather than a numeric range: the merchant question is "show me the 5xx", and the
 * shared filter vocabulary has no numeric control.
 */
export const ConfigWebhookDeliveryResponseBucket = {
    success: '2xx',
    redirect: '3xx',
    clientError: '4xx',
    serverError: '5xx',
    noResponse: 'none',
};
/** The endpoint refuses anything older, so the UI must not offer a wider preset. */
export const configWebhookDeliveryMaxLookbackDays = 90;
export const configWebhookDeliveryDefaultLookbackDays = 7;
export const configWebhookDeliveryFilters = z.object({
    statuses: z.array(z.enum(ConfigWebhookDeliveryStatus)).min(1).optional(),
    topics: z.array(webhookTopicSchema).min(1).optional(),
    createdOn: dateRange.optional(),
    responseBuckets: z
        .array(z.enum(ConfigWebhookDeliveryResponseBucket))
        .min(1)
        .optional(),
});
export const configWebhookDeliverySearchReq = z.object({
    filters: configWebhookDeliveryFilters,
    cursor: z
        .object({
        id: z.number(),
        direction: z.union([z.literal('next'), z.literal('prev')]),
    })
        .nullable(),
    orderBy: z.union([z.literal('asc'), z.literal('desc')]),
    take: z.number().default(50),
});
export const configWebhookDeliverySearchResp = z.object({
    data: z.array(configWebhookDelivery),
    pageInfo,
    // counted over the same filters as `data`, so the tiles always reconcile with the rows
    statusCounts: z.record(z.enum(ConfigWebhookDeliveryStatus), z.number()),
});
//# sourceMappingURL=configWebhook.js.map